import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { MdDownload, MdClose } from "react-icons/md";

import RightPanelLayout from "../Layout/RightPanelLayout";
import Button from "../Utilities/Button";
import PaginationNew from "../Utilities/PaginationNew";
import Modal from "../Utilities/Modal";
import Toast from "../Utilities/Toast";
import Datepicker from "../Utilities/Datepicker/Datepicker";

import {
  fetchInvoices,
  fetchInvoiceDetail,
  exportInvoices,
  voidInvoice,
  syncAllInvoices,
  setFilter,
  setPage,
  resetDetail,
  selectInvoices,
} from "../../store/slice/manager/invoices";
import { selectPermissionSettings } from "../../store/slice/permissionSettings";

import styles from "./style/InvoicesList.module.scss";

const LIMIT = 20;

// 與 LeftNavigation 的可見性規則一致：只有 admin（不是任何中控分身）跟 finance 能進
const FINANCE_EMAIL = "workspace+finance@adbert.com.tw";
const TDP_NON_ADMIN_EMAILS = new Set([
  "workspace+manage@adbert.com.tw",
  "workspace+marketing@adbert.com.tw",
  "workspace+ceo@adbert.com.tw",
  "workspace+ai@adbert.com.tw",
]);

const STATUS_OPTIONS = [
  { value: "", label: "全部狀態" },
  { value: "issued", label: "已開立" },
  { value: "unset", label: "未開立" },
  { value: "void", label: "已作廢" },
  { value: "allowance", label: "已折讓" },
];

const TYPE_OPTIONS = [
  { value: "", label: "全部類型" },
  { value: "shop", label: "儲值金" },
  { value: "subscription", label: "訂閱" },
  { value: "customized", label: "企業委託單" },
];

const BUYER_OPTIONS = [
  { value: "", label: "全部對象" },
  { value: "personal", label: "個人" },
  { value: "enterprise", label: "企業" },
];

const STATUS_LABEL = {
  issued: "已開立",
  unset: "未開立",
  void: "已作廢",
  allowance: "已折讓",
};

const CARRIER_LABEL = {
  barcode: "手機條碼",
  provider: "雲端發票",
  none: "無載具",
  other: "其他",
};

const formatDateTime = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const InvoicesList = () => {
  const dispatch = useDispatch();
  const { auth } = useSelector(selectPermissionSettings);
  const { rows, total, page, loading, filter, detail, detailLoading } =
    useSelector(selectInvoices);

  const email = auth?.email;
  const allowed = email === FINANCE_EMAIL || !TDP_NON_ADMIN_EMAILS.has(email);

  // 只有 search 文字框需要 local draft（避免每打一個字都打 API）
  const [draftSearch, setDraftSearch] = useState(filter.search || "");

  const toYmd = (d) => {
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const [itemsOpen, setItemsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [voidingIds, setVoidingIds] = useState([]);
  const [syncingAll, setSyncingAll] = useState(false);
  const [toast, setToast] = useState({ show: false, title: "", error: false });

  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast((p) => ({ ...p, show: false })), 2500);
    return () => clearTimeout(t);
  }, [toast.show]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total]
  );

  const load = useCallback(
    (overridePage) => {
      const candidate = overridePage ?? page;
      const safePage =
        Number.isInteger(candidate) && candidate > 0 ? candidate : 1;
      dispatch(
        fetchInvoices({
          filter,
          page: safePage,
          limit: LIMIT,
        })
      );
    },
    [dispatch, filter, page]
  );

  useEffect(() => {
    if (!allowed) return;
    load();
  }, [load, allowed]);

  if (email && !allowed) {
    return <Navigate to='/dashboard/tdp/home' replace />;
  }

  const applySearch = () => {
    dispatch(setFilter({ search: draftSearch.trim() }));
  };

  const handlePageChange = (event) => {
    // ReactPaginate 回傳 { selected: 0-based index }
    const next =
      event && typeof event === "object" && "selected" in event
        ? event.selected + 1
        : Number(event);
    if (Number.isInteger(next) && next > 0) {
      dispatch(setPage(next));
    }
  };

  const handleOpenItems = async (invoiceId) => {
    setItemsOpen(true);
    await dispatch(fetchInvoiceDetail(invoiceId));
  };

  const handleCloseItems = () => {
    setItemsOpen(false);
    dispatch(resetDetail());
  };

  const handleSyncAll = async () => {
    if (syncingAll) return;
    setSyncingAll(true);
    try {
      const res = await dispatch(syncAllInvoices({ filter })).unwrap();
      const parts = [`同步 ${res?.synced ?? 0} 張`];
      if (res?.discovered) parts.push(`新發現 ${res.discovered} 張`);
      if (res?.failed) parts.push(`失敗 ${res.failed}`);
      setToast({ show: true, title: parts.join("、"), error: false });
      load();
    } catch (e) {
      setToast({ show: true, title: "同步失敗：" + (e?.message || "請查看後端 log"), error: true });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleVoidInvoice = async (row) => {
    if (voidingIds.includes(row.id)) return;
    const ok = window.confirm(
      `確定要作廢發票「${row.invoice_num || "(未開立)"}」嗎？此動作不可復原。`
    );
    if (! ok) return;
    setVoidingIds((prev) => [...prev, row.id]);
    try {
      await dispatch(voidInvoice({
        invoiceId: row.id,
        invoiceNum: row.invoice_num,
        invoiceRandNum: row.invoice_rand_num,
        reason: "中控作廢",
      })).unwrap();
      setToast({ show: true, title: "已作廢", error: false });
      load();
    } catch (e) {
      setToast({ show: true, title: "作廢失敗：" + (e?.message || "請查看後端 log"), error: true });
    } finally {
      setVoidingIds((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await dispatch(exportInvoices({ filter })).unwrap();
      setToast({ show: true, title: "匯出完成", error: false });
    } catch {
      setToast({ show: true, title: "匯出失敗，請重試", error: true });
    } finally {
      setExporting(false);
    }
  };

  return (
    <RightPanelLayout title='發票管理'>
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>發票列表</div>
        <div className={styles.toolbarActions}>
          <input
            type='search'
            className={styles.input}
            placeholder='公司名稱 / 統編 / 發票序號'
            value={draftSearch}
            onChange={(e) => setDraftSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applySearch();
            }}
            onBlur={applySearch}
          />
          <span className={styles.toolbarLabel}>類型</span>
          <select
            className={styles.select}
            value={filter.type || ""}
            onChange={(e) => dispatch(setFilter({ type: e.target.value }))}
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className={styles.toolbarLabel}>對象</span>
          <select
            className={styles.select}
            value={filter.buyer_type || ""}
            onChange={(e) => dispatch(setFilter({ buyer_type: e.target.value }))}
          >
            {BUYER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className={styles.toolbarLabel}>狀態</span>
          <select
            className={styles.select}
            value={filter.status || ""}
            onChange={(e) => dispatch(setFilter({ status: e.target.value }))}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className={styles.toolbarLabel}>建立日期</span>
          <div className={styles.datepickerWrap}>
            <Datepicker
              selectDate={filter.date_from ? new Date(filter.date_from) : null}
              setSelectDate={(d) =>
                dispatch(setFilter({ date_from: toYmd(d) }))
              }
              placeholderText='起始日'
              dateFormat='yyyy-MM-dd'
            />
          </div>
          <span className={styles.toolbarLabel}>~</span>
          <div className={styles.datepickerWrap}>
            <Datepicker
              selectDate={filter.date_to ? new Date(filter.date_to) : null}
              setSelectDate={(d) => dispatch(setFilter({ date_to: toYmd(d) }))}
              placeholderText='結束日'
              dateFormat='yyyy-MM-dd'
            />
          </div>
          <Button
            type='secondary'
            title={syncingAll ? "同步中…" : "全部同步綠界"}
            onClick={handleSyncAll}
            disabled={syncingAll}
          />
          <Button
            type='primary'
            title={exporting ? "匯出中…" : "匯出 xlsx"}
            icon={<MdDownload fontSize={18} />}
            onClick={handleExport}
            disabled={exporting}
          />
        </div>
      </div>

      {!loading && total > 0 ? (
        <div className={styles.tablePaginationTop}>
          <PaginationNew
            onPageChange={handlePageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      ) : null}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>名稱</th>
              <th>企業/個人</th>
              <th>統一編號</th>
              <th>載具</th>
              <th>發票序號</th>
              <th>狀態</th>
              <th>建立時間</th>
              <th>購買商品</th>
              <th>動作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className={styles.tableLoadingCell}>
                  載入中…
                </td>
              </tr>
            ) : null}

            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.tableEmptyCell}>
                  尚無符合的發票資料
                </td>
              </tr>
            ) : null}

            {!loading &&
              rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.buyer_name || "-"}</td>
                  <td>
                    {row.buyer_kind ? (
                      <span
                        className={`${styles.buyerKindBadge} ${
                          styles[row.buyer_kind] || ""
                        }`}
                      >
                        {row.buyer_kind === "enterprise" ? "企業" : "個人"}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{row.buyer_tax_id || "-"}</td>
                  <td>
                    {CARRIER_LABEL[row.carrier_type] || row.carrier_type || "-"}
                  </td>
                  <td>{row.invoice_num || "-"}</td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        styles[row.status] || ""
                      }`}
                    >
                      {STATUS_LABEL[row.status] || row.status}
                    </span>
                  </td>
                  <td>{formatDateTime(row.created_at)}</td>
                  <td>
                    <button
                      type='button'
                      className={styles.itemsButton}
                      onClick={() => handleOpenItems(row.id)}
                    >
                      查看
                    </button>
                  </td>
                  <td>
                    {row.status === "issued" && (
                      <button
                        type='button'
                        className={styles.voidButton}
                        disabled={voidingIds.includes(row.id)}
                        onClick={() => handleVoidInvoice(row)}
                      >
                        {voidingIds.includes(row.id) ? "作廢中…" : "作廢"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={itemsOpen} onClose={handleCloseItems} size='md'>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>購買商品</div>
          <button
            type='button'
            className={styles.modalClose}
            onClick={handleCloseItems}
            aria-label='關閉'
          >
            <MdClose />
          </button>
        </div>
        <div className={styles.modalBody}>
          {detailLoading ? (
            <div>載入中…</div>
          ) : !detail || !detail.items || detail.items.length === 0 ? (
            <div style={{ color: "#888" }}>此發票無對應商品資訊。</div>
          ) : (
            detail.items.map((item, idx) => (
              <div key={idx} className={styles.itemRow}>
                <div>
                  <div className={styles.itemName}>{item.name}</div>
                  <div className={styles.itemMeta}>
                    NT$ {Number(item.price).toLocaleString()} × {item.count}
                    {item.word}
                  </div>
                </div>
                <div className={styles.itemName}>
                  NT$ {Number(item.price * item.count).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      </Modal>

      <Toast show={toast.show} title={toast.title} error={toast.error} />
    </RightPanelLayout>
  );
};

export default InvoicesList;
